import React, { useState } from "react";
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Snackbar, TextField, Typography, useTheme, Stack, Paper, IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { useItemValidator } from "../../hooks/useItemValidator";
import { fetchWithAuth } from "../../utils/api";

const AdminMarkNotes = () => {
    const theme = useTheme();

    const [notes, setNotes] = useState("");
    const [input, setInput] = useState("");
    const [markedItems, setMarkedItems] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: "" });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [summaryResult, setSummaryResult] = useState({ success: [], failed: [] });
    const { validateItemOnly, loading } = useItemValidator();

    const handleSearch = () => {
        if (loading) {
            setSnackbar({ open: true, message: "Item list is still loading..." });
            return;
        }

        const { validItems, errors } = validateItemOnly(input);

        if (errors.length > 0) {
            setSnackbar({ open: true, message: errors.join(", ") });
        }

        if (validItems.length > 0) {
            setMarkedItems((prev) => [
                ...prev,
                ...validItems.filter((item) => !prev.includes(item)),
            ]);
        }

        setInput("");
    };

    const handleRemove = (itemToRemove) => {
        setMarkedItems((prev) => prev.filter((item) => item !== itemToRemove));
    };

    const handleSubmit = () => {
        setConfirmOpen(true);
    };

    const confirmSubmit = async () => {
        setConfirmOpen(false);
        try {
            const response = await fetchWithAuth("/items/mark-notes", {
                method: "POST",
                body: {
                    notes,
                    items: markedItems,
                },
            });

            if (response && response.success) {
                setSummaryResult({
                    success: response.success || [],
                    failed: response.failed || [],
                });
                setSummaryOpen(true);
            } else {
                throw new Error("Unexpected server response.");
            }
        } catch (err) {
            console.error("Failed to mark notes:", err);
            setSnackbar({ open: true, message: "Error marking notes." });
        }
        setMarkedItems([]);
    };

    return (
        <Box sx={{ px: 2, py: 1 }}>
            <Typography
                variant="h5"
                sx={{
                    fontWeight: "bold",
                    color: theme.palette.accent.main,
                    textAlign: "center",
                    mb: 2,
                }}
            >
                admin: mark items with notes
            </Typography>

            <Box
                sx={{
                    maxWidth: "800px",
                    mx: "auto",
                    p: 2,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 2,
                    boxShadow: 3,
                }}
            >
                <Stack spacing={2}>
                    <TextField
                        fullWidth
                        label="Enter notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        size="small"
                    />
                    <Box
                        component="form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSearch();
                        }}
                        display="flex"
                        gap={2}
                        flexDirection={{ xs: "column", sm: "row" }}
                        alignItems="center"
                    >
                        <TextField
                            fullWidth
                            label="Enter item names (comma separated)"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            size="small"
                        />
                        <Button
                            variant="contained"
                            type="submit"
                            startIcon={<SearchIcon />}
                            sx={{ minWidth: 120 }}
                        >
                            Search
                        </Button>
                    </Box>
                </Stack>

                {markedItems.length > 0 && (
                    <Box mt={4}>
                        <Typography variant="h6" gutterBottom>
                            Items to be marked
                        </Typography>
                        <Stack spacing={1}>
                            {markedItems.map((item) => (
                                <Paper
                                    key={item.toLowerCase()}
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        backgroundColor: theme.palette.background.default,
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography>{item}</Typography>
                                    <IconButton size="small" onClick={() => handleRemove(item)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Paper>
                            ))}
                        </Stack>

                        <Box mt={3} display="flex" justifyContent="space-between" gap={2}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => setMarkedItems([])}
                                fullWidth
                            >
                                Clear All
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                fullWidth
                            >
                                Submit
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Confirm Modal */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent dividers>
                    <Typography gutterBottom>
                        You are about to update the following items with the note:
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
                        <Typography variant="body2">{notes}</Typography>
                    </Paper>
                    <ul>
                        {markedItems.map((item) => (
                            <li key={item}>
                                <Typography>{item}</Typography>
                            </li>
                        ))}
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={confirmSubmit} variant="contained" color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Summary Modal */}
            <Dialog open={summaryOpen} onClose={() => setSummaryOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Marking Summary</DialogTitle>
                <DialogContent dividers>
                    {summaryResult.success.length > 0 && (
                        <>
                            <Typography variant="subtitle1" gutterBottom>
                                Marked Items:
                            </Typography>
                            <ul>
                                {summaryResult.success.map((item) => (
                                    <li key={item}>
                                        <Typography>{item}</Typography>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {summaryResult.failed.length > 0 && (
                        <>
                            <Typography variant="subtitle1" gutterBottom color="error">
                                Failed to Mark:
                            </Typography>
                            <ul>
                                {summaryResult.failed.map((item) => (
                                    <li key={item}>
                                        <Typography>{item}</Typography>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSummaryOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ open: false, message: "" })}
                message={snackbar.message}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </Box>
    );
};

export default AdminMarkNotes;